import { type DailyCostTotal, formatCostUsd } from '@src/services/opsService';

const CHART_WIDTH = 640;
const CHART_HEIGHT = 180;
const CHART_PADDING_X = 8;
const CHART_PADDING_TOP = 16;
const CHART_PADDING_BOTTOM = 24;

export interface ChartPoint {
  x: number;
  y: number;
  day: string;
  totalCostUsd: number;
}

export interface TrendChartGeometry {
  points: ChartPoint[];
  areaPath: string;
  linePath: string;
  yAxisTicks: { y: number; value: number }[];
  plotBottom: number;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export function formatCostAxisLabel(value: number): string {
  return currencyFormatter.format(value);
}

/**
 * Pure geometry for the trend line: maps `data` (ascending by day) onto an SVG viewBox of
 * CHART_WIDTH x CHART_HEIGHT, with headroom above/below the data range so the line and its
 * "today" callout never touch the plot edges. Kept separate from the rendering component so
 * the math can be unit tested without mounting anything.
 */
export function computeTrendGeometry(data: DailyCostTotal[]): TrendChartGeometry {
  const plotTop = CHART_PADDING_TOP;
  const plotBottom = CHART_HEIGHT - CHART_PADDING_BOTTOM;
  const plotHeight = plotBottom - plotTop;

  if (data.length === 0) {
    return { points: [], areaPath: '', linePath: '', yAxisTicks: [], plotBottom };
  }

  const values = data.map((d) => d.totalCostUsd);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const headroom = Math.max((dataMax - dataMin) * 0.2, 5);
  const axisMin = Math.max(0, Math.floor((dataMin - headroom) / 10) * 10);
  const axisMax = Math.max(axisMin + 10, Math.ceil((dataMax + headroom) / 10) * 10);
  const axisRange = axisMax - axisMin;

  const plotWidth = CHART_WIDTH - CHART_PADDING_X * 2;
  const stepX = data.length > 1 ? plotWidth / (data.length - 1) : 0;

  const points: ChartPoint[] = data.map((d, i) => {
    const x = CHART_PADDING_X + stepX * i;
    const ratio = (d.totalCostUsd - axisMin) / axisRange;
    const y = plotBottom - ratio * plotHeight;
    return { x: round(x), y: round(y), day: d.day, totalCostUsd: d.totalCostUsd };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1].x},${plotBottom} L${points[0].x},${plotBottom} Z`;

  const yAxisTicks = [axisMax, (axisMax + axisMin) / 2, axisMin].map((value) => ({
    value,
    y: round(plotBottom - ((value - axisMin) / axisRange) * plotHeight),
  }));

  return { points, areaPath, linePath, yAxisTicks, plotBottom };
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

interface CostTrendChartProps {
  data: DailyCostTotal[];
}

/** 14-day total cost trend line, replicating the validated prototype's hand-drawn SVG line
 * chart (gridlines + $ axis labels + filled area + "today" callout) rather than pulling in a
 * charting library -- no charting dependency exists elsewhere in this org's dashboards, and a
 * single line with this little chrome doesn't need one. */
export function CostTrendChart({ data }: CostTrendChartProps) {
  const geometry = computeTrendGeometry(data);

  if (geometry.points.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No cost data available.</p>;
  }

  const last = geometry.points[geometry.points.length - 1];
  const secondToLast = geometry.points[geometry.points.length - 2];
  const pctChange =
    secondToLast && secondToLast.totalCostUsd > 0
      ? ((last.totalCostUsd - secondToLast.totalCostUsd) / secondToLast.totalCostUsd) * 100
      : undefined;

  return (
    <svg
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      width="100%"
      height={CHART_HEIGHT}
      style={{ overflow: 'visible' }}
      role="img"
      aria-label={`Total daily cost trend over the last ${data.length} days, ending at ${formatCostUsd(last.totalCostUsd)} today`}
    >
      {geometry.yAxisTicks.map((tick) => (
        <line
          key={tick.y}
          x1={CHART_PADDING_X}
          y1={tick.y}
          x2={CHART_WIDTH - CHART_PADDING_X}
          y2={tick.y}
          stroke="hsl(var(--border))"
          strokeWidth={1}
        />
      ))}
      {geometry.yAxisTicks.map((tick) => (
        <text key={`label-${tick.y}`} x={0} y={tick.y + 3} fill="hsl(var(--muted-foreground))" fontSize={10}>
          {formatCostAxisLabel(tick.value)}
        </text>
      ))}

      <path d={geometry.areaPath} fill="hsl(var(--chart-1))" opacity={0.14} />
      <polyline
        points={geometry.points.map((p) => `${p.x},${p.y}`).join(' ')}
        fill="none"
        stroke="hsl(var(--chart-1))"
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={last.x} cy={last.y} r={4} fill="hsl(var(--chart-5))" />
      <text
        x={last.x}
        y={Math.max(10, last.y - 10)}
        fill="hsl(var(--chart-5))"
        fontSize={10.5}
        fontWeight={600}
        textAnchor="end"
      >
        Today {formatCostUsd(last.totalCostUsd)}
        {pctChange !== undefined ? ` (${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(0)}%)` : ''}
      </text>

      <text x={CHART_PADDING_X} y={CHART_HEIGHT - 8} fill="hsl(var(--muted-foreground))" fontSize={10}>
        {data.length} days ago
      </text>
      <text
        x={CHART_WIDTH - CHART_PADDING_X}
        y={CHART_HEIGHT - 8}
        fill="hsl(var(--muted-foreground))"
        fontSize={10}
        textAnchor="end"
      >
        Today
      </text>
    </svg>
  );
}
